// ─────────────────────────────────────────────────────────────────────────────
// Translation loading + cache.
//
// Every namespace is its own `import()`, so Parcel code-splits each feature's
// strings into its own asset and a locale is only ever downloaded once someone
// actually selects it. English is not on the wire for a receptionist who never
// leaves Persian.
//
// Two caches, both process-lifetime:
//   `cache`    locale/namespace → the parsed bundle. A second switch back to a
//              language is synchronous — nothing is re-fetched or re-parsed.
//   `inflight` locale/namespace → the in-progress promise, so N simultaneous
//              requests for the same file share one network round trip.
//
// The map below is written out by hand rather than built from a template
// literal because Parcel resolves `import()` statically: a computed specifier
// would silently produce a bundle with no translations in it.
// ─────────────────────────────────────────────────────────────────────────────

import { NAMESPACES, isSupportedLocale } from './config';

const IMPORTERS = {
  fa: {
    common:       () => import('./translations/fa/common.json'),
    nav:          () => import('./translations/fa/nav.json'),
    validation:   () => import('./translations/fa/validation.json'),
    errors:       () => import('./translations/fa/errors.json'),
    auth:         () => import('./translations/fa/auth.json'),
    shortcuts:    () => import('./translations/fa/shortcuts.json'),
    settings:     () => import('./translations/fa/settings.json'),
    reception:    () => import('./translations/fa/reception.json'),
    patients:     () => import('./translations/fa/patients.json'),
    calendar:     () => import('./translations/fa/calendar.json'),
    doctors:      () => import('./translations/fa/doctors.json'),
    people:       () => import('./translations/fa/people.json'),
    payroll:      () => import('./translations/fa/payroll.json'),
    cashier:      () => import('./translations/fa/cashier.json'),
    payments:     () => import('./translations/fa/payments.json'),
    accounting:   () => import('./translations/fa/accounting.json'),
    insurance:    () => import('./translations/fa/insurance.json'),
    operations:   () => import('./translations/fa/operations.json'),
    goods:        () => import('./translations/fa/goods.json'),
    store:        () => import('./translations/fa/store.json'),
    reports:      () => import('./translations/fa/reports.json'),
    subscription: () => import('./translations/fa/subscription.json'),
    clinic:       () => import('./translations/fa/clinic.json'),
    secretary:    () => import('./translations/fa/secretary.json'),
  },
  en: {
    common:       () => import('./translations/en/common.json'),
    nav:          () => import('./translations/en/nav.json'),
    validation:   () => import('./translations/en/validation.json'),
    errors:       () => import('./translations/en/errors.json'),
    auth:         () => import('./translations/en/auth.json'),
    shortcuts:    () => import('./translations/en/shortcuts.json'),
    settings:     () => import('./translations/en/settings.json'),
    reception:    () => import('./translations/en/reception.json'),
    patients:     () => import('./translations/en/patients.json'),
    calendar:     () => import('./translations/en/calendar.json'),
    doctors:      () => import('./translations/en/doctors.json'),
    people:       () => import('./translations/en/people.json'),
    payroll:      () => import('./translations/en/payroll.json'),
    cashier:      () => import('./translations/en/cashier.json'),
    payments:     () => import('./translations/en/payments.json'),
    accounting:   () => import('./translations/en/accounting.json'),
    insurance:    () => import('./translations/en/insurance.json'),
    operations:   () => import('./translations/en/operations.json'),
    goods:        () => import('./translations/en/goods.json'),
    store:        () => import('./translations/en/store.json'),
    reports:      () => import('./translations/en/reports.json'),
    subscription: () => import('./translations/en/subscription.json'),
    clinic:       () => import('./translations/en/clinic.json'),
    secretary:    () => import('./translations/en/secretary.json'),
  },
  ar: {
    common:       () => import('./translations/ar/common.json'),
    nav:          () => import('./translations/ar/nav.json'),
    validation:   () => import('./translations/ar/validation.json'),
    errors:       () => import('./translations/ar/errors.json'),
    auth:         () => import('./translations/ar/auth.json'),
    shortcuts:    () => import('./translations/ar/shortcuts.json'),
    settings:     () => import('./translations/ar/settings.json'),
    reception:    () => import('./translations/ar/reception.json'),
    patients:     () => import('./translations/ar/patients.json'),
    calendar:     () => import('./translations/ar/calendar.json'),
    doctors:      () => import('./translations/ar/doctors.json'),
    people:       () => import('./translations/ar/people.json'),
    payroll:      () => import('./translations/ar/payroll.json'),
    cashier:      () => import('./translations/ar/cashier.json'),
    payments:     () => import('./translations/ar/payments.json'),
    accounting:   () => import('./translations/ar/accounting.json'),
    insurance:    () => import('./translations/ar/insurance.json'),
    operations:   () => import('./translations/ar/operations.json'),
    goods:        () => import('./translations/ar/goods.json'),
    store:        () => import('./translations/ar/store.json'),
    reports:      () => import('./translations/ar/reports.json'),
    subscription: () => import('./translations/ar/subscription.json'),
    clinic:       () => import('./translations/ar/clinic.json'),
    secretary:    () => import('./translations/ar/secretary.json'),
  },
  fr: {
    common:       () => import('./translations/fr/common.json'),
    nav:          () => import('./translations/fr/nav.json'),
    validation:   () => import('./translations/fr/validation.json'),
    errors:       () => import('./translations/fr/errors.json'),
    auth:         () => import('./translations/fr/auth.json'),
    shortcuts:    () => import('./translations/fr/shortcuts.json'),
    settings:     () => import('./translations/fr/settings.json'),
    reception:    () => import('./translations/fr/reception.json'),
    patients:     () => import('./translations/fr/patients.json'),
    calendar:     () => import('./translations/fr/calendar.json'),
    doctors:      () => import('./translations/fr/doctors.json'),
    people:       () => import('./translations/fr/people.json'),
    payroll:      () => import('./translations/fr/payroll.json'),
    cashier:      () => import('./translations/fr/cashier.json'),
    payments:     () => import('./translations/fr/payments.json'),
    accounting:   () => import('./translations/fr/accounting.json'),
    insurance:    () => import('./translations/fr/insurance.json'),
    operations:   () => import('./translations/fr/operations.json'),
    goods:        () => import('./translations/fr/goods.json'),
    store:        () => import('./translations/fr/store.json'),
    reports:      () => import('./translations/fr/reports.json'),
    subscription: () => import('./translations/fr/subscription.json'),
    clinic:       () => import('./translations/fr/clinic.json'),
    secretary:    () => import('./translations/fr/secretary.json'),
  },
  de: {
    common:       () => import('./translations/de/common.json'),
    nav:          () => import('./translations/de/nav.json'),
    validation:   () => import('./translations/de/validation.json'),
    errors:       () => import('./translations/de/errors.json'),
    auth:         () => import('./translations/de/auth.json'),
    shortcuts:    () => import('./translations/de/shortcuts.json'),
    settings:     () => import('./translations/de/settings.json'),
    reception:    () => import('./translations/de/reception.json'),
    patients:     () => import('./translations/de/patients.json'),
    calendar:     () => import('./translations/de/calendar.json'),
    doctors:      () => import('./translations/de/doctors.json'),
    people:       () => import('./translations/de/people.json'),
    payroll:      () => import('./translations/de/payroll.json'),
    cashier:      () => import('./translations/de/cashier.json'),
    payments:     () => import('./translations/de/payments.json'),
    accounting:   () => import('./translations/de/accounting.json'),
    insurance:    () => import('./translations/de/insurance.json'),
    operations:   () => import('./translations/de/operations.json'),
    goods:        () => import('./translations/de/goods.json'),
    store:        () => import('./translations/de/store.json'),
    reports:      () => import('./translations/de/reports.json'),
    subscription: () => import('./translations/de/subscription.json'),
    clinic:       () => import('./translations/de/clinic.json'),
    secretary:    () => import('./translations/de/secretary.json'),
  },
};

const cache = new Map();
const inflight = new Map();

const cacheKey = (locale, namespace) => `${locale}/${namespace}`;

// Parcel hands back a module namespace object; the JSON is on `default` for a
// JSON asset, but a plain object comes through untouched under Vitest's transform.
const unwrap = (mod) => (mod && mod.default) || mod || {};

/**
 * One namespace, cached. Never rejects: a namespace that fails to load resolves
 * to `{}` and every key in it falls through to the fallback locale, because a
 * failed download of a translation file must not take the reception desk with it.
 */
export function loadNamespace(locale, namespace) {
  const key = cacheKey(locale, namespace);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);

  const importer = IMPORTERS[locale]?.[namespace];
  if (!importer) {
    cache.set(key, {});
    return Promise.resolve({});
  }

  const promise = importer()
    .then((mod) => unwrap(mod))
    .catch((err) => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[i18n] failed to load ${key}:`, err);
      }
      return {};
    })
    .then((bundle) => {
      cache.set(key, bundle);
      inflight.delete(key);
      return bundle;
    });

  inflight.set(key, promise);
  return promise;
}

/**
 * Every namespace for a locale, as `{ [namespace]: bundle }`.
 *
 * All namespaces are fetched together rather than per-screen on purpose. The
 * whole catalogue is a few tens of kilobytes and the alternative — a screen
 * painting its keys while its namespace is still in flight — is a visible flash
 * of `settings.language.title` on a clinical screen. Per-namespace loading is
 * still available via `loadNamespace` if a future feature is heavy enough to
 * want it; the cache and the key format do not change either way.
 */
export function loadCatalog(locale) {
  if (!isSupportedLocale(locale)) return Promise.resolve({});
  return Promise.all(
    NAMESPACES.map((ns) => loadNamespace(locale, ns).then((bundle) => [ns, bundle])),
  ).then(Object.fromEntries);
}

/**
 * The catalogue for a locale if it is already cached, else null. Lets the
 * provider swap languages synchronously on a second visit — no loading state,
 * no re-render with stale strings.
 */
export function peekCatalog(locale) {
  const entries = NAMESPACES.map((ns) => [ns, cache.get(cacheKey(locale, ns))]);
  return entries.every(([, bundle]) => bundle !== undefined) ? Object.fromEntries(entries) : null;
}

/**
 * Warm a locale without switching to it — the settings picker calls this when
 * an option is focused, so choosing it is instant. Fire-and-forget.
 */
export function preloadCatalog(locale) {
  loadCatalog(locale).catch(() => {});
}
